// src/jornada/iniciarJornada.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { jornadaStore } from "../data/jornadaStore";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, Stack, TextField, Button } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

export const IniciarJornada = ({
  onJornadaActualizada,
  onSuccess,
  onError,
  uiLocked = false,
}) => {
  // =========================
  // Hooks / estado base
  // =========================
  const toast = useToast();

  const [centros, setCentros] = useState([]);
  const [centroId, setCentroId] = useState("");

  const [coordenadas, setCoordenadas] = useState({ latitud: null, longitud: null });
  const [inicioLocal, setInicioLocal] = useState(null);

  const [loadingCentros, setLoadingCentros] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toastDebounce = useRef(null);

  const flash = (msg, ms = 4500, kind = "warning") => {
    // evita spamear toasts si el usuario pulsa repetidas veces
    if (toastDebounce.current) clearTimeout(toastDebounce.current);
    toastDebounce.current = setTimeout(() => {
      if (kind === "error") toast.error(msg);
      else if (kind === "success") toast.success(msg);
      else if (kind === "info") toast.info(msg);
      else toast.warning(msg);
    }, 0);

    // auto-clear de debounce
    toastDebounce.current = setTimeout(() => {
      toastDebounce.current = null;
    }, ms);
  };

  // =========================
  // Carga centros (offline-first store)
  // =========================
  useEffect(() => {
    let alive = true;

    (async () => {
      setLoadingCentros(true);
      try {
        const data = await jornadaStore.getCentros();
        if (alive) setCentros(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn("[IniciarJornada] getCentros:", e?.message);
        if (alive) setCentros([]);
      } finally {
        if (alive) setLoadingCentros(false);
      }
    })();

    return () => {
      alive = false;
      if (toastDebounce.current) clearTimeout(toastDebounce.current);
    };
  }, []);

  // =========================
  // Helpers
  // =========================
  const calcularDistanciaKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // km
  };

  const centroSeleccionado = useMemo(() => {
    return (centros || []).find((c) => String(c.id) === String(centroId)) || null;
  }, [centros, centroId]);

  const centrosOrdenados = useMemo(() => {
    return (centros || [])
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [centros]);

  const limpiarFormulario = () => {
    setCentroId("");
    setCoordenadas({ latitud: null, longitud: null });
    setInicioLocal(null);
    toast.info("Formulario limpiado.");
  };

  // =========================
  // Acción principal
  // =========================
  const capturarUbicacionYCrearJornada = () => {
    if (!centroId) return flash("Selecciona un centro.");
    if (isSubmitting || uiLocked) return;

    // Evita duplicar inicio si ya hay activa (local o servidor)
    const snap = jornadaStore.snapshot ? jornadaStore.snapshot() : { active: null, local: null };
    const active = snap.active;
    const local = snap.local;

    const activeOpen = !!active && !active?.pendingFinish && !active?.pendingCancel;
    const localPending = !!local && (local.pendingFinish || local.pendingCancel);

    if (activeOpen) {
      onError?.("Ya tienes un fichaje en curso. Ve a 'Finalizar' para cerrarlo.");
      onJornadaActualizada?.();
      return;
    }

    if (localPending) {
      onError?.(
        "Tienes una jornada pendiente de envío. Espera a que se sincronice antes de iniciar otra."
      );
      onJornadaActualizada?.();
      return;
    }

    if (!centroSeleccionado?.latitud || !centroSeleccionado?.longitud) {
      flash("Este centro no tiene coordenadas asignadas.", 4500, "error");
      return;
    }

    if (!navigator.geolocation) {
      onError?.("Tu navegador no soporta geolocalización.");
      return;
    }

    setIsSubmitting(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const latitud = position.coords.latitude;
          const longitud = position.coords.longitude;

          const distanciaKm = calcularDistanciaKm(
            latitud,
            longitud,
            parseFloat(centroSeleccionado.latitud),
            parseFloat(centroSeleccionado.longitud)
          );

          if (distanciaKm > 1) {
            const confirmar = window.confirm(
              `Estás a ${distanciaKm.toFixed(2)} km del centro. ¿Deseas iniciar igualmente?`
            );
            if (!confirmar) {
              flash("Registro cancelado por el usuario.", 2500, "info");
              setIsSubmitting(false);
              return;
            }
          }

          // fingerprint para el backend
          const fp = await FingerprintJS.load();
          const { visitorId: fingerprint } = await fp.get();

          // ✅ delega en la store: ella decide online vs offline y gestiona outbox
          const result = await jornadaStore.start({
            centroId: Number(centroId),
            centroNombre: centroSeleccionado?.nombre || "",
            latitud,
            longitud,
            fingerprint,
          });

          if (result?.status === "noop") {
            onError?.("Ya tienes una jornada en curso o pendiente de envío.");
            await onJornadaActualizada?.();
            setIsSubmitting(false);
            return;
          }

          setInicioLocal(new Date());
          setCoordenadas({ latitud, longitud });

          if (result?.status === "online") {
            onSuccess?.("Jornada iniciada correctamente.");
          } else if (result?.status === "offline") {
            onSuccess?.("Sin conexión: fichaje guardado y se enviará automáticamente.");
          } else {
            onSuccess?.("Jornada registrada.");
          }

          await onJornadaActualizada?.();
        } catch (e) {
          console.error(e);
          onError?.("No se pudo iniciar la jornada.");
        } finally {
          setIsSubmitting(false);
        }
      },
      (err) => {
        console.error(err);
        onError?.("No se pudo obtener la ubicación.");
        setIsSubmitting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const botonDeshabilitado =
    uiLocked ||
    isSubmitting ||
    !centroId ||
    !Array.isArray(centros) ||
    centros.length === 0 ||
    loadingCentros;

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        {/* ✅ Encabezado homogéneo (igual que listados) */}
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Iniciar Jornada Laboral
        </Typography>

        {/* ✅ Contenedor estrecho (1 columna) centrado por theme */}
        <Box className="contentContainer contentContainer--formSingle">
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              {/* Aviso útil si no hay centros cacheados y estás offline */}
              {!centros?.length && !navigator.onLine && (
                <Typography color="error" sx={{ fontSize: 14 }}>
                  No hay centros en caché. Abre la app con conexión al menos una vez para precargarlos.
                </Typography>
              )}

              {/* Centro */}
              <Autocomplete
                size="small"
                options={centrosOrdenados}
                getOptionLabel={(c) => c?.nombre || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={centroSeleccionado}
                onChange={(_, newValue) => setCentroId(newValue ? newValue.id : "")}
                loading={loadingCentros}
                disabled={!centros?.length || isSubmitting || uiLocked}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Centro"
                    placeholder="Escribe para buscar…"
                    required
                  />
                )}
                clearOnEscape
                noOptionsText={loadingCentros ? "Cargando..." : "No hay centros disponibles"}
              />

              {/* Acciones */}
              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: "flex-end", flexWrap: "wrap" }}
              >
                <Button
                  variant="outlined"
                  onClick={limpiarFormulario}
                  disabled={isSubmitting || uiLocked}
                >
                  Limpiar
                </Button>

                <Button
                  onClick={capturarUbicacionYCrearJornada}
                  disabled={botonDeshabilitado}
                >
                  {isSubmitting ? "Iniciando..." : "Capturar ubicación e iniciar"}
                </Button>
              </Stack>

              {/* Info local (opcional) */}
              {inicioLocal && (
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Inicio local: {new Date(inicioLocal).toLocaleString()}
                  {coordenadas.latitud ? (
                    <>
                      {" "}
                      — ({coordenadas.latitud.toFixed(5)}, {coordenadas.longitud?.toFixed(5)})
                    </>
                  ) : null}
                </Typography>
              )}
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );

};
